/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IContentBlockViewProps } from '@msdyn365-commerce-modules/content-block';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { ICoreContext, Image } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { HeadingSettingsFontWeight, IContentBlockProps } from '../definition-extensions/content-block.ext.props.autogenerated';
import { IContentBlockData } from './content-block.data';
import { ICategoryHierarchyViewProps } from '../../../modules/dobbies-search-result-container/dobbies-search-result-container';
import { Link, Separator } from '../../../modules/dobbies-search-result-container/components';
import { CategoryHierarchy as CategoryHierarchyData } from '@msdyn365-commerce/commerce-entities';
import ReadMoreText from './components/read-more-text.component';

const ContentBlockView: React.FC<IContentBlockViewProps & IContentBlockProps<IContentBlockData>> = props => {
    const {
        config: {
            isCategoryNameTitle,
            hideClubPlusBanner,
            clubPlusTierId,
            toggleReadMore,
            readMoreCharacterLimit,
            paragraph,
            heading,
            headingSettings
        },
        resources: { readMoreText, readLessText },
        data: {
            modulesSharedState: { result: modulesSharedState }
        },
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        context
        // title
    } = props;

    const {
        request: {
            urlTokens: { pageType }
        }
    } = context;

    const fontWeightMap: Record<string, number> = {
        [HeadingSettingsFontWeight.Medium500]: 500,
        [HeadingSettingsFontWeight.Bold700]: 700
    };

    const renderHeading = (): React.ReactNode => {
        if (!heading) {
            return null;
        }

        const headingElement = (
            <Node
                className={classnames('ms-content-block__title', {
                    'ms-content-block__title--underline-on-hover': headingSettings?.underlineOnHover
                })}
                tag={heading.tag ?? 'h2'}
                style={
                    headingSettings?.fontWeight && {
                        fontWeight: `${fontWeightMap[headingSettings.fontWeight]} !important`
                    }
                }
                dangerouslySetInnerHTML={{ __html: heading.text }}
            />
        );

        if (headingSettings?.linkUrl && headingSettings.linkUrl !== '') {
            return (
                <a href={headingSettings.linkUrl} className='ms-content-block__title-link'>
                    {headingElement}
                </a>
            );
        }

        return headingElement;
    };

    /**
     * Renders the text content with Read More/Read Less functionality if enabled and character limit is configured
     */
    const renderTextContent = (): React.ReactNode => {
        // If toggleReadMore is enabled, readMoreCharacterLimit is configured and paragraph exists, use ReadMoreText component
        if (toggleReadMore && readMoreCharacterLimit && readMoreCharacterLimit > 0 && paragraph) {
            // paragraph is of type RichText which is essentially a string containing HTML
            const paragraphContent = typeof paragraph === 'string' ? paragraph : String(paragraph);
            return (
                <ReadMoreText
                    htmlContent={paragraphContent}
                    characterLimit={readMoreCharacterLimit}
                    className='ms-content-block__text'
                    readMoreText={readMoreText}
                    readLessText={readLessText}
                />
            );
        }
        // Otherwise, render the default text
        return text;
    };

    /* VSI Cutomization - START - 11/11/20 */
    const [isMobile, SetIsMobile] = useState(false);
    const [categoryName, SetCategoryName] = useState<string | undefined>();
    const [isClubPlus, SetIsClubPlus] = useState<boolean | undefined>(undefined);
    const [isImageAvailable, setIsImageAvailable] = useState<boolean | undefined>();

    const _getCategoryHierarchy = (): ICategoryHierarchyViewProps => {
        const { data } = props;
        const categoryHierarchy = data.categoryHierarchy.result;
        const categoryLinks =
            categoryHierarchy &&
            categoryHierarchy.map((value: CategoryHierarchyData, index: number) => {
                return <Link key={index} text={value.Name} ariaLabel={`"Go To: ${value.Name}`} href={value.Url} />;
            });
        const categoryLinkSeparator = <Separator separator='/' />;

        return {
            CategoryHierarchyContainer: { tag: 'nav', className: 'ms-search-result-container__category-hierarchy' },
            categoryHierarchyList: categoryLinks,
            categoryHierarchySeparator: categoryLinkSeparator
        };
    };

    const getCategoryName = (category: React.ReactNode): string => {
        const el = category as React.ReactElement<{ text?: string }>;
        return el && el.props && el.props.text ? String(el.props.text) : '';
    };

    const renderCategoryHierarchy = (categoryHeirarchyData: ICategoryHierarchyViewProps): JSX.Element | null => {
        const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = categoryHeirarchyData;
        const [expanded, setExpanded] = React.useState(false);
        const isCategoryPage = MsDyn365.isBrowser && window.location.pathname.includes('.c');

        const homeIconElement = isCategoryPage ? (
            <a className='home-breadcrumb' key='home-breadcrumb' href='/' aria-label='Go to home page'>
                <span className='icon--home' aria-hidden='true' />
            </a>
        ) : null;

        const fullBreadcrumbList: React.ReactNode[] = homeIconElement
            ? [homeIconElement, ...(categoryHierarchyList || [])]
            : [...(categoryHierarchyList || [])];

        const [breadcrumbList] = React.useState<React.ReactNode[] | undefined>(fullBreadcrumbList);

        // Guard: Ensure all required data is loaded before rendering
        if (!categoryHierarchyList || categoryHierarchyList.length === 0 || !CategoryHierarchyContainer) {
            return null;
        }

        // Guard: Ensure home icon dependencies are available on category page
        if (isCategoryPage && !categoryHierarchySeparator) {
            return null;
        }

        const totalLength = categoryHierarchyList.reduce<number>((acc, category) => acc + getCategoryName(category).length, 0);
        const validCharacterLength = totalLength <= 35;

        const shouldCollapse = Boolean(isMobile) && !expanded && !validCharacterLength && categoryHierarchyList.length > 2;

        const handleExpandClick = (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded(true);
        };

        if (shouldCollapse) {
            // For collapsed view: home icon + first category + ellipsis + last category
            const firstCategory = categoryHierarchyList[0];
            const lastCategory = categoryHierarchyList[categoryHierarchyList.length - 1];

            return (
                <Node
                    {...CategoryHierarchyContainer}
                    className={classnames('ms-search-result-container__category-hierarchy', 'category-hierarchy-collapsed')}
                >
                    {homeIconElement}
                    {homeIconElement && categoryHierarchySeparator}
                    <Node className='category-hierarchy__expand' role='button' onClick={handleExpandClick}>
                        {firstCategory}
                    </Node>
                    {categoryHierarchySeparator}
                    <Node className='category-hierarchy__ellipsis' role='button' onClick={handleExpandClick}>
                        …
                    </Node>
                    <Node className='category-hierarchy__last'>{lastCategory}</Node>
                </Node>
            );
        }

        return (
            <Node {...CategoryHierarchyContainer}>
                {breadcrumbList?.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {breadcrumbList && breadcrumbList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    };

    useEffect(() => {
        const _isMobile = isMobileView(context);
        SetIsMobile(_isMobile);

        // Check if pageType is Category and state is not set yet, get its category name
        if (categoryName === undefined && pageType && pageType.toLowerCase() === 'category') {
            if (isCategoryNameTitle) {
                SetCategoryName(modulesSharedState?.categoryName);
            }
            if (isImageAvailable === undefined) {
                const isImage = props.config.image ? true : false;
                setIsImageAvailable(isImage);
            }
        }
        // Check if clubplus banner is set to hide on my account page for club plus members and state is not set yet i.e. undefined
        if (hideClubPlusBanner && isClubPlus === undefined) {
            modulesSharedState?.customerLoyaltyTierId === clubPlusTierId?.toLowerCase() ? SetIsClubPlus(true) : SetIsClubPlus(false);
        }
    });

    // Dont show banner for club plus members on my account or if tier id is not set yet
    if (hideClubPlusBanner && (isClubPlus === undefined || isClubPlus === true)) {
        return <></>;
    }

    const mobileImage = _renderImageForMobile(props);
    const categoryHeading = renderHeading();

    // const doNoImageMagic = isImageAvailable === false && !isMobile;

    // const checkMobileImageAvailable = isImageAvailable === false && isMobile === true;
    // const detailsStyles = checkMobileImageAvailable ? {display : 'none'}: {}
    // const detailsStyles = doNoImageMagic ? {} : { display: 'none' };

    /* VSI Cutomization - END */
    const checkClassName = () => {
        if (isImageAvailable === true && isMobile === false) {
            return classnames(contentBlockContainer.className, '');
        } else if (isImageAvailable === true && isMobile === true) {
            return classnames(contentBlockContainer.className, '');
        } else if (isImageAvailable === false && isMobile === false) {
            return classnames(contentBlockContainer.className, '');
        } else if (isImageAvailable === false && isMobile === true) {
            return classnames(contentBlockContainer.className, '');
        } else {
            return classnames(contentBlockContainer.className, '');
        }
    };
    let hasSearchResultsContainer;
    if (MsDyn365.isBrowser && window) {
        const hasContentBlockImage = !document.getElementsByClassName('no-image-categories')?.length;
        hasSearchResultsContainer =
            document.getElementsByClassName('ms-search-result-container__product-section') &&
            document.getElementsByClassName('ms-search-result-container__product-section').length &&
            hasContentBlockImage;
        if (!hasSearchResultsContainer) {
            hasSearchResultsContainer =
                document.getElementsByClassName('ms-search-result-container__Products') &&
                document.getElementsByClassName('ms-search-result-container__Products').length &&
                hasContentBlockImage;
        }
    }

    const categoryHeirarchy = _getCategoryHierarchy();
    if (imageLink) {
        return (
            <>
                {hasSearchResultsContainer ? <div>{MsDyn365.isBrowser && renderCategoryHierarchy(categoryHeirarchy)}</div> : <></>}
                <Module {...contentBlockContainer} className={checkClassName()}>
                    <Node
                        {...contentBlockAnchorTag}
                        href={imageLink}
                        className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                        aria-label={imageAriaLabel}
                    >
                        {/* VSI Cutomization - START - 11/11/20 */}
                        <Node {...imageContainer}>{isMobile === true && mobileImage ? mobileImage : image}</Node>
                        {/* VSI Cutomization - END */}
                    </Node>
                    <Node {...detailsContainer}>
                        {/* {!(hasSearchResultsContainer && hasSearchResultsContainer.length) ? categoryHeading : <></>} */}
                        {categoryHeading}
                        {renderTextContent()}
                        {links}
                    </Node>
                </Module>
            </>
        );
    } else {
        return (
            <>
                {hasSearchResultsContainer ? <div>{MsDyn365.isBrowser && renderCategoryHierarchy(categoryHeirarchy)}</div> : <></>}
                <Module {...contentBlockContainer} className={checkClassName()}>
                    {/* VSI Cutomization - START - 11/11/20 */}
                    <Node {...imageContainer}>{isMobile === true && mobileImage ? mobileImage : image}</Node>
                    {/* VSI Cutomization - END */}
                    <Node {...detailsContainer}>
                        {/* {!(hasSearchResultsContainer && hasSearchResultsContainer.length) ? categoryHeading : <></>} */}
                        {categoryHeading}
                        {renderTextContent()}
                        {links}
                    </Node>
                </Module>
            </>
        );
    }
};

/* VSI Cutomization - START - 11/11/20 */

/* Following function renders if mobile view has different image than that of desktop image.
This function is specifically written for sale banner on homepage but can be used anywhere user want to add separate image for mobile. */
const _renderImageForMobile = (props: IContentBlockViewProps & IContentBlockProps<{}>): JSX.Element | undefined => {
    const {
        config: { imageForMobile },
        context: { request }
    } = props;
    if (imageForMobile === undefined) {
        return undefined;
    }

    return (
        <Image
            className={'promotion-mobile-image'}
            {...imageForMobile}
            gridSettings={request.gridSettings!}
            imageSettings={imageForMobile.imageSettings}
            loadFailureBehavior='default'
            role='tabpanel'
            id={`${props.id}__static-image`}
        />
    );
};

/* Following function checks device type and handles state accordingly if view changes, state will be updated and view will be shown according to device. */
const isMobileView = (context: ICoreContext): boolean => {
    if (typeof window !== undefined && window.innerWidth) {
        const gridSettings = context.request.gridSettings;

        if (gridSettings) {
            if (gridSettings.xs && window.innerWidth < gridSettings.xs.w) {
                return true;
            } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                return false;
            } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                return false;
            } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                return false;
            } else {
                return false;
            }
        }
    }

    return false;
};

/* VSI Cutomization - END */

export default ContentBlockView;
